import { ReactElement, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Space, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined, SwapOutlined } from '@ant-design/icons';
import { Guard } from '@ohpen/react-authz';
import { Table, TableComponent, TableProps, ValueLists } from '@ohpen/enso-ui';

import { useListTableState } from '@pmp/hooks';

import { RegionRow, regionColumns } from '../regionsBdmsTableConfig';

const rowKey = 'id';

interface Props {
  data: RegionRow[];
  valueLists: ValueLists;
  loading: boolean;
  canReplace: boolean;
  onCreate: () => void;
  onEdit: (record: RegionRow) => void;
  onReplace: (record: RegionRow) => void;
  onDelete: (record: RegionRow) => void;
}

export const RegionsList = ({ data, valueLists, loading, canReplace, onCreate, onEdit, onReplace, onDelete }: Props): ReactElement => {
  const { t } = useTranslation();
  const tableRef = useRef<TableComponent | null>(null);
  const tableState = useListTableState<RegionRow>('REGIONS', rowKey, tableRef, regionColumns);

  const getRowActions = useCallback(
    (record: RegionRow): (ReactElement | null)[] => {
      const deleteBlocked = (record.bdmIds ?? []).length > 0;
      return [
        <Guard key="edit" permissions={['lenders:update']} flags={['enableLenderCrud']}>
          <Tooltip title={t('global.buttons.edit')}>
            <Button type="text" size="small" aria-label="Edit region" icon={<EditOutlined />} onClick={() => onEdit(record)} />
          </Tooltip>
        </Guard>,
        <Guard key="replace" permissions={['lenders:delete']} flags={['enableLenderCrud']}>
          <Tooltip title={canReplace ? t('lender.regionsBdms.labels.replace') : t('lender.regionsBdms.labels.replaceNeedsAnotherRegion')}>
            <Button
              type="text"
              size="small"
              aria-label="Replace region"
              icon={<SwapOutlined />}
              onClick={() => onReplace(record)}
              disabled={!canReplace}
            />
          </Tooltip>
        </Guard>,
        <Guard key="delete" permissions={['lenders:delete']} flags={['enableLenderCrud']}>
          <Tooltip
            title={
              deleteBlocked
                ? t('lender.regionsBdms.labels.regionDeleteBlockedTooltip', { count: (record.bdmIds ?? []).length })
                : t('global.buttons.delete')
            }
          >
            <Button
              type="text"
              size="small"
              aria-label="Delete region"
              icon={<DeleteOutlined />}
              onClick={() => onDelete(record)}
              disabled={deleteBlocked}
            />
          </Tooltip>
        </Guard>,
      ];
    },
    [canReplace, onDelete, onEdit, onReplace, t],
  );

  const tableProps: TableProps<string, RegionRow> = useMemo(
    () => ({
      ...tableState,
      id: 'regions',
      ref: tableRef,
      dataSource: data,
      columns: regionColumns,
      rowKey,
      pagination: true,
      scroll: { x: 'max-content', y: '100%' },
      loading,
      valueLists,
      getRowActions,
    }),
    [tableState, data, valueLists, loading, getRowActions],
  );

  return (
    <Table {...tableProps}>
      <Table.ActionsBar>
        <Flex justify="space-between">
          <Space size="middle">
            <Table.GlobalSearch />
          </Space>
          <Space size="middle">
            <Table.ResetFiltersButton />
            <Table.SelectColumnsButton />
            <Guard permissions={['lenders:create']} flags={['enableLenderCrud']}>
              <Table.CreateButton onClick={onCreate} />
            </Guard>
          </Space>
        </Flex>
      </Table.ActionsBar>
    </Table>
  );
};
